/**
 * `mailto:` URL building — one place, because hand-rolled string templates got
 * the escaping wrong (#351).
 *
 * The subject of a `mailto:` is very often data SOMEBODY ELSE wrote: a job
 * title typed by the manager who raised the request, a property address, an
 * invite message. Interpolating it raw has two consequences:
 *
 *   - an unescaped `&` or `#` TRUNCATES the URL, and the recipient gets a
 *     draft with an amputated subject;
 *   - a `&bcc=…` or `&body=…` inside that third-party text is read as an extra
 *     mailto PARAMETER, so a title someone else controls can add a blind
 *     recipient or a body to a draft opened on another user's machine. Not code
 *     execution — but data reaching a structure it has no business modifying.
 *
 * So every segment goes through an encoder here, address included. Callers pass
 * plain text and never build the URL themselves.
 *
 * ---------------------------------------------------------------------------
 * DECIDED, #372 — the plain `[href]="'mailto:' + email"` templates stay as they
 * are. Read this before re-opening the question.
 * ---------------------------------------------------------------------------
 *
 * Twelve templates still concatenate an address with no encoder:
 *
 *   admin/…/user-detail-enhanced.component.html:301
 *   pages/agency/leads/lead-detail/lead-detail.component.html:22
 *   pages/agency/my-day/my-day.component.ts:377
 *   pages/apps/contracts/…/contract-detail-overview.component.html:55
 *   pages/apps/repairs/…/work-request-panel.component.ts:110
 *   pages/apps/tenants/…/tenant-detail-overview.component.html:213
 *   pages/service-providers/provider-view/provider-view.component.html:258
 *   pages/syndic/building-detail/assigned-providers-tab.component.ts:153
 *   pages/tenant/tenant-access/tenant-access.component.html:44
 *   pages/tenant/tenant-contracts/tenant-contract-detail.component.html:144
 *   shared/components/syndic-building-card/syndic-building-card.component.ts:87 and :140
 *
 * `pages/provider/…/job-request-detail.component.html` left the list by the
 * boy-scout rule below: the row was being edited anyway (it now shows the
 * requester workspace's alias instead of a personal address), so it binds
 * `requesterMailtoHref()` rather than concatenating.
 *
 * They are NOT the defect this helper was written for, and they were left
 * alone deliberately rather than by omission:
 *
 *   - None of them writes a `?`. The injection that motivated #351 needs the
 *     app to open a query the attacker's text can then extend; here the only
 *     way in is an address that itself contains `?` or `&`, which is a
 *     different and much narrower shape than "a title someone else typed".
 *   - Every one of them is an inert anchor. Nothing fires until a human clicks
 *     it, and eight of the twelve render the address AS the link text, so a
 *     malformed one is on screen before the click. The four that do not —
 *     `user-detail-enhanced` (translated label), `lead-detail`, `my-day` and
 *     `work-request-panel` (icon-only buttons) — are the ones to migrate first
 *     if this is ever reopened.
 *   - Migrating them costs twelve more screens of browser recipe on a fix
 *     whose recipe is already the expensive half. A security fix that ships
 *     half-recetted is worse than one with a stated, bounded scope.
 *
 * The rule that follows from that, for NEW code:
 *
 *   - As soon as a `mailto:` carries ANY parameter — subject, body, cc, bcc —
 *     it goes through `buildMailtoUrl`. No exceptions, no "the subject is a
 *     constant": constants become interpolations.
 *   - A bare address in a template MAY stay a concatenation. When you would
 *     rather encode it anyway, expose a component method returning
 *     `buildMailtoUrl(address)` and bind that — see `hrefFor()` in
 *     `shared/components/ai-widgets/blocks/ai-address-card-block.component.ts`.
 *     There is no pipe on purpose: one more indirection for a case we just
 *     decided not to sweep would be a helper nobody borrows, which is exactly
 *     how #351 came back as #372.
 *   - The twelve above are bounded debt, cleared by the boy-scout rule: if
 *     you are already editing one of those templates, bind a method. Do not
 *     open a PR that only does that.
 */

export interface MailtoParams {
  /** Subject line, plain text. Encoded here — pass it unescaped. */
  subject?: string | null;
  /** Body, plain text. Encoded here — pass it unescaped. */
  body?: string | null;
  /** Extra recipients, plain addresses. */
  cc?: string | null;
  bcc?: string | null;
}

/**
 * Percent-encodes a query segment.
 *
 * `encodeURIComponent` leaves `+` alone, and several mail clients read a `+`
 * in a query component as a space — "Fuite + odeur" would arrive as
 * "Fuite   odeur". `%2B` is unambiguous everywhere.
 */
function encodeSegment(value: string): string {
  return encodeURIComponent(value).replace(/\+/g, '%2B');
}

/**
 * Encodes an address for the `to`/`cc`/`bcc` slots.
 *
 * `@` and `.` stay literal on purpose: RFC 6068 allows them unencoded in an
 * addr-spec, and a `%40` trips a number of desktop clients that hand the path
 * to the OS without decoding it. Everything else — `?`, `&`, `#`, `,`, spaces,
 * newlines — is encoded, which is what stops an address field from opening a
 * second parameter or a second recipient.
 */
export function encodeMailAddress(address: string): string {
  return encodeSegment(address).replace(/%40/g, '@');
}

/**
 * Builds `mailto:<to>?subject=…&body=…`, every segment encoded.
 *
 * Returns `''` for an empty address so callers can bail out on a falsy result
 * rather than opening a draft addressed to nobody.
 */
export function buildMailtoUrl(to: string | null | undefined, params: MailtoParams = {}): string {
  const address = (to ?? '').trim();
  if (!address) return '';

  const query = (
    [
      ['subject', params.subject],
      ['body', params.body],
      ['cc', params.cc],
      ['bcc', params.bcc]
    ] as const
  )
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) =>
      key === 'cc' || key === 'bcc'
        ? `${key}=${encodeMailAddress(String(value))}`
        : `${key}=${encodeSegment(String(value))}`
    )
    .join('&');

  return query ? `mailto:${encodeMailAddress(address)}?${query}` : `mailto:${encodeMailAddress(address)}`;
}

/**
 * Opens the draft by CLICKING AN ANCHOR, not by assigning
 * `window.location.href`.
 *
 * Assigning the location navigates the document: on a machine with no mail
 * client registered for the scheme, the browser leaves the SPA and the user
 * comes back to a cold boot of the app. A synthetic anchor click is handled by
 * the browser as a protocol activation and leaves the page — and the app
 * state — exactly where it was.
 *
 * Returns false when there was no address to write to, so a caller can say so.
 */
export function openMailDraft(to: string | null | undefined, params: MailtoParams = {}): boolean {
  const url = buildMailtoUrl(to, params);
  if (!url) return false;

  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  link.click();
  return true;
}
